import { Component, Input, OnInit, signal } from "@angular/core";
import { ControlPoint } from "src/app/bo/controlPoint";
import { VibrationData } from "src/app/bo/vibrationsForChart/vibration-data";
import { UtilisateurService } from "src/app/services/utilisateur.service";
import { VibrationsRecordService } from "src/app/services/vibrations-record.service";
import { RecordModalComponent } from "../record-modal/record-modal.component";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { Site } from "src/app/bo/site";
import { AssignRecordsModalComponent } from "../assign-records-modal/assign-records-modal.component";

@Component({
  selector: 'app-vibration-detail',
  templateUrl: './vibration-detail.page.html',
  styleUrls: ['./vibration-detail.page.scss'],
  standalone: false
})
export class VibrationDetailPage implements OnInit {
    @Input() set vibrationRecords(val: VibrationData[]) { this._vibrationRecords.set(val); }
    @Input() set totalRecords(val: number) { this._totalRecords.set(val); }
    @Input() controlPoints: ControlPoint[];
    @Input() tirId: number;
    @Input() tirName: string;

    // Signals — all template-bound mutable state
    protected readonly _vibrationRecords = signal<VibrationData[] | undefined>(undefined);
    protected readonly _totalRecords = signal(0);
    protected readonly filteredRecords = signal<VibrationData[]>([]);
    protected readonly pages = signal<number[]>([]);
    protected readonly totalPages = signal(0);
    protected readonly currentPage = signal(1);
    protected readonly selectedOption = signal('all');
    protected readonly isDetailsVisible = signal<boolean[]>([]);
    protected readonly activeControlPointIndex = signal<number | undefined>(undefined);
    protected readonly assignedControlPoints = signal<ControlPoint[]>([]);

    // Non-template internal state
    pageSize = 30;
    deviceSerialNumber: string;
    siteId!: number;
    controlPointId: number;
    site: Site;

   constructor(private readonly vibrationsRecordService: VibrationsRecordService,
               private readonly utilisateurService: UtilisateurService,
               private readonly dialogRecorde: MatDialog
  ) {
        this.siteId = this.utilisateurService.currentSite()?.siteId;
  }

    ngOnInit(): void {
      this.site = this.utilisateurService.currentSite();
      this.applyFilter();
      this.assignedControlPoints.set(this.getAssignedControlPoints());
    }

    getAssignedControlPoints(): ControlPoint[] {
      const records = this._vibrationRecords();
      if (!records?.length || !this.controlPoints?.length) {
          return [];
      }
      const uniqueControlPointIds = new Set<number>(
        records
            .filter(record => record.controlPointId)
            .map(record => record.controlPointId)
      );
      return this.controlPoints.filter(cp => uniqueControlPointIds.has(cp.controlPointId));
    }

    applyFilter(): void {
      const totalPgs = Math.ceil(this._totalRecords() / this.pageSize);
      this.totalPages.set(totalPgs);
      this.pages.set(Array.from({ length: totalPgs }, (_, i) => i + 1));
      this.filterRecords('all');
    }

    filterRecords(option: string): void {
      this.selectedOption.set(option);
      const records = this._vibrationRecords() ?? [];
      if (option === 'assigned') {
        this.filteredRecords.set(records.filter(record => record.controlPointId != null));
      } else if (option === 'unAssigned') {
        this.filteredRecords.set(records.filter(record => record.controlPointId == null));
      } else {
        const filtered = this.deviceSerialNumber
          ? records.filter(record => record.recordMetadata.seismographSerialNumber === this.deviceSerialNumber)
          : records;
        this.filteredRecords.set([...filtered]);
      }
    }

    onRecordClick(): void {
      const dialogConfig: MatDialogConfig = {
          data: {
            siteId: this.site.siteId,
            eventId: null,
            controlPointId: null,
            blastId: this.tirId,
            siteName: this.site.nom,
            controlPointName: null,
            regionName: this.site.region?.nom,
            controlPoints: this.controlPoints,
          },
        };

        const dialogRef = this.dialogRecorde.open(RecordModalComponent, dialogConfig);
        dialogRef.afterClosed().subscribe(result => {
          if (result) {
            this.currentPage.set(1);
            this.loadVibrationRecords();
          }
        });
    }

    goToPage(page: number): void {
      if (page < 1 || page > this.totalPages()) return;
      this.currentPage.set(page);
      this.loadVibrationRecords();
    }

    loadVibrationRecords(): void {
      this.vibrationsRecordService.GetRecords(this.siteId, null, this.tirId, null, this.currentPage(), this.pageSize, false, false, false)
        .subscribe({
          next: (response) => {
            this._vibrationRecords.set(response.data.records);
            this._totalRecords.set(response.data.total);
            const totalPgs = Math.ceil(response.data.total / this.pageSize);
            this.totalPages.set(totalPgs);
            this.pages.set(Array.from({ length: totalPgs }, (_, i) => i + 1));
            this.filterRecords('all');
            this.assignedControlPoints.set(this.getAssignedControlPoints());
          },
          error: (err) => {
            console.error('Error loading vibration records', err);
          }
        });
    }

    assignMultiRecords(): void {
      this.vibrationsRecordService
        .GetRecords(this.site.siteId, null, null, null, 1, 10, false, true, true)
        .subscribe({
          next: (response: { data: { records: any[] } }) => {
            const dialogConfig: MatDialogConfig = {
              data: {
                siteId: this.site.siteId,
                eventId: null,
                controlPointId: null,
                records: response.data.records.filter(record => record.controlPointId == null || record.blastId == null),
                blastId: this.tirId,
                tirName: this.tirName
              },
            };

            const dialogRef = this.dialogRecorde.open(AssignRecordsModalComponent, dialogConfig);
            dialogRef.afterClosed().subscribe(result => {
              if (result) {
                this.currentPage.set(1);
                this.loadVibrationRecords();
              }
            });
          },
          error: (err: any) => {
            console.error('Failed to load records for dialog', err);
          },
        });
    }

    protected changeTab(controlPointId: number, index: number): void {
      const isSameControlPoint = this.activeControlPointIndex() === index;
      this.activeControlPointIndex.set(isSameControlPoint ? undefined : index);
      const currentControlPointId = isSameControlPoint ? null : controlPointId;
      this.currentPage.set(1);
      this.pageSize = 30;
      this.vibrationsRecordService
        .GetRecords(
          this.site.siteId,
          currentControlPointId,
          this.tirId,
          null,
          this.currentPage(),
          this.pageSize,
          false,
          false, false
        )
        .subscribe({
          next: ({ data: { records } }) => {
            this._vibrationRecords.set(records);
            this.filterRecords('all');
            this.isDetailsVisible.set([]);
          },
          error: (err) => console.error('Failed to load records for control point', err),
        });
    }
}